import { makeStyles } from '@material-ui/styles'
import { RootState } from 'app/store'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import {
  listKaapiMachineDeployments,
  updateKaapiMachineDeployment,
} from 'k8s/components/kaapi/machine-deployment/actions'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { prop } from 'ramda'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import PhysicalNodesTable from '../cluster/deployment/form-components/PhysicalNodesTable'
import { kaapiMachineDeploymentsForClusterSelector } from '../kaapi/machine-deployment/selectors'
import { listByoHosts, addClusterLabelToByoHost } from '../nodes/byoh/actions'

const initialValues = {
  physicalNodes: [],
}

interface AddNodeGroupsModalProps {
  onClose: () => void
  clusterName: string
}

const AddNodeGroupsModal = ({ onClose, clusterName }: AddNodeGroupsModalProps) => {
  const classes = useStyles()
  const { params, getParamsUpdater } = useParams<Record<string, any>>(initialValues)
  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = activeKaapiTenant

  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  // Fetch existing Machine Deployments
  const { reload: reloadMachineDeployments } = useListAction(listKaapiMachineDeployments)
  const machineDeploymentsSelector = kaapiMachineDeploymentsForClusterSelector({
    clusterName,
  })
  const machineDeployments = useSelector(machineDeploymentsSelector) || []
  const { reload: reloadByoHosts } = useListAction(listByoHosts)

  // Update Actions
  const {
    update: updateMachineDeployment,
    updating: updatingMachineDeployment,
    error: updateMachineDeploymentError,
  } = useUpdateAction(updateKaapiMachineDeployment)
  const {
    update: addClusterLabelToByoHostFn,
    updating: addClusterLabelToByoHostUpdating,
    error: addClusterLabelToByoHostError,
  } = useUpdateAction(addClusterLabelToByoHost)

  const hasError = !!addClusterLabelToByoHostError || !!updateMachineDeploymentError || !!error

  useEffect(() => {
    if (!hasError) return
    // Show first error
    const errorObj = addClusterLabelToByoHostError || updateMachineDeploymentError || error

    setError({
      title: errorObj?.title || 'Error adding new nodes',
      message: errorObj?.message || 'An error occurred while adding new nodes. Please try again.',
    })
  }, [hasError, addClusterLabelToByoHostError, updateMachineDeploymentError, error])

  const handleSubmit = async () => {
    setError(null)
    setLoading(true)

    // Check if physical nodes are provided
    if (params.physicalNodes.length === 0) {
      setError({ title: 'Error', message: 'Please select atleast one physical node' })
      setLoading(false)
      return
    }

    // Update the selected nodes
    const promises = []
    params?.physicalNodes?.forEach(async (node) => {
      promises.push(
        addClusterLabelToByoHostFn({
          name: node?.name,
          clusterName,
        }),
      )
    })

    const res = await Promise.all(promises)
    // If any of the promises failed, return
    if (res?.some((item) => !item?.success)) {
      setError({ title: 'Error', message: 'Failed to update labels on physical nodes' })
      setLoading(false)
      return
    }

    // Update the machine deployment
    await updateMachineDeployment({
      namespace,
      name: machineDeployments[0]?.metadata?.name, // Only one machine deployment is expected for a BYO cluster
      customBody: [
        {
          op: 'replace',
          path: '/spec/replicas',
          value: machineDeployments[0]?.spec?.replicas + params?.physicalNodes?.length,
        },
      ],
    })

    if (!hasError) {
      // Reload the machine deployments and machines
      reloadByoHosts(true, false)
      reloadMachineDeployments(true, false)
      onClose()
    }
  }

  const submitting = addClusterLabelToByoHostUpdating || updatingMachineDeployment || loading

  return (
    <ModalForm
      open
      title="Add Physical Nodes"
      maxWidth={820}
      className={classes.modal}
      onSubmit={handleSubmit}
      submitTitle="Add Physical Nodes"
      error={error}
      submitting={submitting}
      disableSubmit={params.physicalNodes.length === 0}
      onClose={onClose}
      data-testid="add-physical-nodes-modal"
    >
      <Text variant="body2">Select the physical nodes to add to the cluster</Text>
      <br />
      <PhysicalNodesTable
        selectedNodes={params.physicalNodes}
        getParamsUpdater={getParamsUpdater}
      />
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  modal: {
    '& .modal-body': {
      padding: '32px 24px 24px 32px',
    },
    '& .modal-body .progress-root .progressContent > form': {
      marginBottom: 0,
    },
  },
}))

export default AddNodeGroupsModal
